import { OrderStatus, type OrderStatus as OS } from '@/types/enums'
import i18n from '@/utils/i18n'

/**
 * Тип покупателя — определяет, какая ветка воронки применима.
 * `undefined` (legacy-заказы без поля) трактуется как B2B-`company` для
 * обратной совместимости: исторически такого поля не было, и все эти
 * заказы — корпоративные.
 */
export type CustomerKind = 'company' | 'individual' | undefined

/** Линейная воронка B2B (ТЗ §5.1) */
export const LINEAR_CHAIN: OS[] = [
  OrderStatus.DRAFT,
  OrderStatus.SEARCH,
  OrderStatus.ASSIGNED,
  OrderStatus.DRIVING,
  OrderStatus.ARRIVED,
  OrderStatus.INSPECTION,
  OrderStatus.APPRAISAL,
  OrderStatus.BUYING,
  OrderStatus.DOCUMENT_PREPARATION,
  OrderStatus.PAYMENT_TRANSFER,
  OrderStatus.TRANSPORTATION,
  OrderStatus.COMPLETED,
]

/**
 * Линейная воронка C2C (физлицо, customer_kind='individual').
 * После INSPECTION сразу COMPLETED — мы продаём услугу осмотра, без оценки,
 * выкупа, документов, перевода и перевозки. Условие «отчёт передан» =
 * `orders.external_inspection_report_url` не пуст (проверяется отдельно).
 */
export const LINEAR_CHAIN_C2C: OS[] = [
  OrderStatus.DRAFT,
  OrderStatus.SEARCH,
  OrderStatus.ASSIGNED,
  OrderStatus.DRIVING,
  OrderStatus.ARRIVED,
  OrderStatus.INSPECTION,
  OrderStatus.COMPLETED,
]

/**
 * B2B-only активные статусы — для C2C-заказа недопустимы (и UI их не
 * предлагает, и Postgres-триггер `orders_c2c_status_guard_bu` блокирует).
 */
export const B2B_ONLY_STATUSES: OS[] = [
  OrderStatus.APPRAISAL,
  OrderStatus.BUYING,
  OrderStatus.DOCUMENT_PREPARATION,
  OrderStatus.PAYMENT_TRANSFER,
  OrderStatus.TRANSPORTATION,
  OrderStatus.REFUSED_BUYOUT,
]

const ACTIVE: OS[] = [
  OrderStatus.DRAFT,
  OrderStatus.SEARCH,
  OrderStatus.ASSIGNED,
  OrderStatus.DRIVING,
  OrderStatus.ARRIVED,
  OrderStatus.INSPECTION,
  OrderStatus.APPRAISAL,
  OrderStatus.BUYING,
  OrderStatus.DOCUMENT_PREPARATION,
  OrderStatus.PAYMENT_TRANSFER,
  OrderStatus.TRANSPORTATION,
]

const TERMINAL: OS[] = [
  OrderStatus.COMPLETED,
  OrderStatus.CANCELLED,
  OrderStatus.REFUSED_BY_INSPECTOR,
  OrderStatus.REFUSED_BY_SELLER,
  OrderStatus.REFUSED_BUYOUT,
]

export function isTerminalStatus(s: OS): boolean {
  return TERMINAL.includes(s)
}

export function isActiveStatus(s: OS): boolean {
  return ACTIVE.includes(s)
}

/** Следующий шаг по основной воронке. Для C2C — короткая цепочка. */
export function getLinearNext(current: OS, kind: CustomerKind = 'company'): OS | null {
  const chain = kind === 'individual' ? LINEAR_CHAIN_C2C : LINEAR_CHAIN
  const i = chain.indexOf(current)
  if (i < 0 || i >= chain.length - 1) return null
  return chain[i + 1] ?? null
}

/** REFUSED_BY_INSPECTOR: только DRIVING (инспектор отказался ехать). Актуально для B2B и C2C. */
export function canRefusedByInspector(current: OS): boolean {
  return current === OrderStatus.DRIVING
}

/** REFUSED_BY_SELLER: ARRIVED, INSPECTION (продавец отказал на месте). Актуально для B2B и C2C. */
export function canRefusedBySeller(current: OS): boolean {
  return (
    current === OrderStatus.ARRIVED ||
    current === OrderStatus.INSPECTION
  )
}

/** REFUSED_BUYOUT: только B2B (от выкупа отказались после оценки) — для C2C неприменимо. */
export function canRefusedBuyout(current: OS, kind: CustomerKind = 'company'): boolean {
  if (kind === 'individual') return false
  return (
    current === OrderStatus.APPRAISAL ||
    current === OrderStatus.BUYING ||
    current === OrderStatus.DOCUMENT_PREPARATION ||
    current === OrderStatus.PAYMENT_TRANSFER
  )
}

/** C2C: статусы, из которых диспетчер может перескочить на INSPECTION / COMPLETED. */
const C2C_JUMP_FROM: OS[] = [OrderStatus.ASSIGNED, OrderStatus.DRIVING, OrderStatus.ARRIVED]

/**
 * Допустимые переходы «вперёд» для админки: линейный следующий + терминальные отказовые ветки.
 * Отмена — отдельное действие.
 *
 * Для C2C (kind='individual') — цепочка короче: после INSPECTION идёт сразу COMPLETED,
 * стадии APPRAISAL/BUYING/DOCUMENT_PREPARATION/PAYMENT_TRANSFER/TRANSPORTATION и
 * REFUSED_BUYOUT не предлагаются. Выезд/прибытие инспекторы не отмечают, поэтому
 * диспетчер из ASSIGNED (и DRIVING/ARRIVED, если отмечены) переводит сразу в
 * INSPECTION или COMPLETED («осмотр готов»).
 */
export function getForwardTargets(current: OS, kind: CustomerKind = 'company'): OS[] {
  if (isTerminalStatus(current)) return []
  const out: OS[] = []
  if (kind === 'individual' && C2C_JUMP_FROM.includes(current)) {
    out.push(OrderStatus.INSPECTION, OrderStatus.COMPLETED)
  } else {
    const next = getLinearNext(current, kind)
    if (next) out.push(next)
  }
  if (canRefusedByInspector(current)) out.push(OrderStatus.REFUSED_BY_INSPECTOR)
  if (canRefusedBySeller(current)) out.push(OrderStatus.REFUSED_BY_SELLER)
  if (canRefusedBuyout(current, kind)) out.push(OrderStatus.REFUSED_BUYOUT)
  return out
}

/**
 * Статусы, из которых разрешён возврат на «Поиск» (SEARCH).
 * Только РАННИЕ стадии — назначен инспектор или идёт работа на месте:
 * ASSIGNED, DRIVING, ARRIVED, INSPECTION.
 *
 * Сознательно НЕ включаем APPRAISAL/BUYING/DOCUMENT_PREPARATION/PAYMENT_TRANSFER/
 * TRANSPORTATION: на этих стадиях возврат искажает биллинг —
 *  - APPRAISAL и PAYMENT_TRANSFER открывают тарифицируемый простой (idle_periods);
 *    повторный вход после возврата создаёт ВТОРОЙ период → простой задвоится
 *    (calculate-billing суммирует все периоды статуса);
 *  - стадии сделки/доставки уже не про «искать инспектора».
 * Возврат из этих статусов — отдельный сценарий (отмена/ручная корректировка биллинга).
 */
const RETURN_TO_SEARCH_FROM: OS[] = [
  OrderStatus.ASSIGNED,
  OrderStatus.DRIVING,
  OrderStatus.ARRIVED,
  OrderStatus.INSPECTION,
]

/**
 * Возврат заказа на «Поиск» (SEARCH) — админ-действие «искать другого инспектора».
 * На бэкенде RPC change_order_status при p_new_status='SEARCH' обнуляет inspector_id,
 * чтобы заказ вернулся в пул свободных, а не завис с прежним исполнителем.
 */
export function canReturnToSearch(current: OS): boolean {
  return RETURN_TO_SEARCH_FROM.includes(current)
}

/**
 * Метка статуса заказа на текущем языке интерфейса. Имя `statusLabelRu`
 * сохранено для совместимости (~24 call-site); теперь i18n-backed — берёт
 * `orderStatus.*` из локалей (ru/en). Вызывающий компонент должен
 * ре-рендериться при смене языка (useTranslation у вызывающего).
 */
export function statusLabelRu(s: OS): string {
  return i18n.t(`orderStatus.${s}`)
}
